<script setup>
import { computed } from 'vue'
import { useRoute } from '#imports'
import berlinLocations from '~/data/berlin'
import brandenburgLocations from '~/data/brandenburg'

const props = defineProps({
  currentLocation: {
    type: String,
    default: ''
  },
  neighborLocations: {
    type: Array,
    default: () => []
  }
})

const route = useRoute()

function hashString(value) {
  let hash = 0

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0
  }

  return hash
}

function createSeededRandom(seed) {
  let state = seed || 1

  return () => {
    state = (state * 1664525 + 1013904223) >>> 0
    return state / 4294967296
  }
}

function shuffleLocations(locations, seedSource) {
  const shuffled = [...locations]
  const random = createSeededRandom(hashString(seedSource))

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(random() * (index + 1))
    ;[shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]]
  }

  return shuffled
}

const locationGroups = {
  berlin: [
    { label: 'Berlin', url: '/berlin/' },
    ...Object.values(berlinLocations).map((location) => ({
      label: location.name,
      url: `/berlin/${location.district}/`
    }))
  ],
  brandenburg: [
    { label: 'Brandenburg', url: '/brandenburg/' },
    ...Object.entries(brandenburgLocations).map(([slug, location]) => ({
      label: location.name,
      url: `/brandenburg/${slug}/`
    }))
  ]
}

const activeState = computed(() => {
  if (route.path.startsWith('/brandenburg')) {
    return 'brandenburg'
  }

  return 'berlin'
})

const locationLinks = computed(() => {
  const locations = locationGroups[activeState.value] || []
  const seedSource = `${route.path}:${props.currentLocation}:${activeState.value}`

  return shuffleLocations(
    locations.filter((location) => location.label !== props.currentLocation),
    seedSource
  )
    .slice(0, 6)
})

const currentYear = new Date().getFullYear()
</script>

<template>
  <footer class="bg-gray-900 text-white pt-12 pb-6">
    <div class="container mx-auto px-4">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8 pb-8">
        <div>
          <h3 class="text-xl font-semibold mb-4 text-gray-100">mietfotobox.com</h3>
          <p class="text-gray-300 mb-4">
            Professionelle Fotoboxen für unvergessliche Momente auf Ihrer Veranstaltung. Mit modernstem Equipment und persönlichem Service.
          </p>
          <div class="flex items-center space-x-4">
            <a href="https://www.facebook.com/mietfotoboxBerlin" target="_blank" class="text-white hover:text-secondary transition-colors">
              <span class="sr-only">Facebook</span>
              <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fill-rule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clip-rule="evenodd" />
              </svg>
            </a>
            <a href="https://www.instagram.com/mietfotobox_berlin" target="_blank" class="text-white hover:text-secondary transition-colors">
              <span class="sr-only">Instagram</span>
              <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fill-rule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clip-rule="evenodd" />
              </svg>
            </a>
          </div>
        </div>
        
        <div>
          <h3 class="text-xl font-semibold mb-4 text-gray-100">Unsere Standorte</h3>
          <ul class="space-y-2">
            <li v-for="location in locationLinks" :key="location.url">
              <a :href="location.url" class="text-gray-300 hover:text-secondary transition-colors">
                Fotobox in {{ location.label }}
              </a>
            </li>
          </ul>
        </div>
        
        <div>
          <h3 class="text-xl font-semibold mb-4 text-gray-100">Kontakt & Info</h3>
          <ul class="space-y-2 mb-4">
            <li>
              <a href="https://www.mietfotobox.com/impressum/" class="text-gray-300 hover:text-secondary transition-colors">
                Impressum
              </a>
            </li>
            <li>
              <a href="https://www.mietfotobox.com/datenschutzerklaerung/" class="text-gray-300 hover:text-secondary transition-colors">
                Datenschutz
              </a>
            </li>
          </ul>
          <div class="text-gray-300">
            <p>Am Dörferweg 14</p>
            <p>13057 Berlin</p>
            <p class="mt-2">Tel: <a href="tel:+4917641620316" class="hover:text-secondary transition-colors">+49 176 41620316</a></p>
          </div>
        </div>
      </div>
      
      <div class="pt-8 mt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
        <p>&copy; {{ currentYear }} mietfotobox.com - Alle Rechte vorbehalten.</p>
      </div>
    </div>
  </footer>
</template>
